import { FeatureCollection, Geometry } from "geojson";
import { GeoJsonScreenProps } from "../../osaway/models/CustomGeoJsonProperties";
import { CustomMouseEvent, mapActive } from "../utils/map-utils";
import { CustomSource } from "../models/CustomSource";

const selectedNodeColor = "#27BDFF";
const notSelectedSecondaryColor = "#ffffff";

const sourceName = CustomSource.Screen;
const sourceConfig: mapboxgl.GeoJSONSourceSpecification = {
  type: "geojson",
  promoteId: "id",
  data: {
    type: "FeatureCollection",
    features: [],
  },
};

const strokeLayerName = `${sourceName}-stroke`;
const strokeLayerConfig: mapboxgl.LineLayerSpecification = {
  source: sourceName,
  id: strokeLayerName,
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": ["get", "color"],
    "line-width": 10,
  },
  filter: ["==", "$type", "LineString"],
};

const lineLayerName = `${sourceName}-line`;
const lineLayerConfig: mapboxgl.LineLayerSpecification = {
  id: lineLayerName,
  source: sourceName,
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": ["get", "secondaryColor"],
    "line-width": 3,
  },
  filter: ["==", "$type", "LineString"],
};

function config(map: mapboxgl.Map) {
  map.addSource(sourceName, sourceConfig);
  map.addLayer(strokeLayerConfig);
  map.addLayer(lineLayerConfig);
}

function resetConfig(map: mapboxgl.Map) {
  map.removeLayer(strokeLayerConfig.id);
  map.removeLayer(lineLayerConfig.id);
  map.removeSource(sourceName);
}

const applyColors = (geoJson: FeatureCollection, selectedWays: string[]) => {
  return {
    ...geoJson,
    features: geoJson.features.map((feature) => {
      const props = feature.properties as GeoJsonScreenProps;
      const isSelected = selectedWays.includes(props.osaWayName);
      const secondaryColor = isSelected
        ? selectedNodeColor
        : notSelectedSecondaryColor;

      return {
        ...feature,
        properties: {
          ...props,
          secondaryColor,
        },
      };
    }),
  };
};

function configOsaWaySelection(
  map: mapboxgl.Map,
  setSelectedWay: (osaWayName: string) => void
) {
  const canvas = map.getCanvasContainer();
  const cursorPointer = () => (canvas.style.cursor = "pointer");
  const cursorDefault = () => (canvas.style.cursor = "");
  map.on("mouseenter", strokeLayerName, cursorPointer);
  map.on("mouseleave", strokeLayerName, cursorDefault);

  const handleSelectRoute = (e: CustomMouseEvent) => {
    e.preventDefault();
    canvas.style.cursor = "grab";
    const feature = e.features?.[0]!;
    const props = feature.properties as GeoJsonScreenProps;

    map.once("mouseup", () => {
      canvas.style.cursor = "";
      setSelectedWay(props.osaWayName);
    });
  };

  map.on("mousedown", strokeLayerName, handleSelectRoute);
}

function update(map: mapboxgl.Map, geoJson: FeatureCollection) {
  if (mapActive(map)) {
    const source = map.getSource(sourceName) as mapboxgl.GeoJSONSource;
    source.setData(geoJson);
  }
}

function extendBounds(
  bounds: mapboxgl.LngLatBounds,
  geoJson: FeatureCollection
) {
  geoJson.features.forEach((feature) => {
    const geometry = feature.geometry as Geometry;
    switch (geometry.type) {
      case "LineString": {
        (geometry.coordinates as [number, number][]).forEach((coords) =>
          bounds.extend(coords)
        );
        break;
      }
      default:
        break;
    }
  });
}

export const screenGeoJsonMapSource = {
  applyColors,
  configOsaWaySelection,
  config,
  resetConfig,
  update,
  extendBounds,
};
